/**
 * Driver definitions + the transparent FP&A "driver model" spec for the CFO
 * forecast workbook.
 *
 * The detailed 3-way statements in the workbook carry the authoritative ML
 * numbers (toggled across High/Expected/Low). This module powers the separate,
 * *playable* layer: six editable drivers that drive a compact summary model
 * (Revenue → COGS → Gross Profit → Opex → EBITDA, plus working-capital and a
 * cash roll-forward). The model is seeded so that, at the ML's own driver
 * values, it reproduces the ML expected anchor; editing a driver then explores a
 * simplified, fully-transparent neighbourhood around it (disclosed on the
 * Methodology sheet).
 */

export type CfoCase = 'high' | 'expected' | 'low'

/** Case order used everywhere (Scenario_List, CHOOSE, comparison columns). */
export const CFO_CASE_ORDER: CfoCase[] = ['high', 'expected', 'low']

export const CFO_CASE_LABELS: Record<CfoCase, string> = {
  high: 'High',
  expected: 'Expected',
  low: 'Low',
}

export type CfoDriverUnit = 'percent' | 'days' | 'ratio'

export type CfoDriverDef = {
  key: string
  label: string
  unit: CfoDriverUnit
  /** Named range exposed to formulas (human-readable, e.g. `Sales_Growth`). */
  namedRange: string
  min: number
  max: number
  /** Fallback default when the ML provides no value (stored in driver-native units). */
  fallback: number
  description: string
  /**
   * Which end of the driver's range is *favourable for cash*. `up` = a higher
   * value generates more cash (growth, margin, DPO); `down` = a higher value
   * generates less cash (DSO, opex); `neutral` = no clear cash direction
   * (seasonality amplitude). The High/Expected/Low scenario toggle uses this to
   * pick the cash-optimistic assumption in the High case and the cash-pessimistic
   * one in the Low case, so "High" always means *more cash* across the workbook
   * (otherwise the High scenario can paradoxically produce the least cash, because
   * it would pair high growth with high DSO and high opex).
   */
  cashFavorable: 'up' | 'down' | 'neutral'
}

/**
 * The six drivers the server already exposes with expected/low/high triplets
 * (`default_driver_assumptions` in ml_forecasting.rs). Percent drivers are stored
 * as fractions (0.15 = 15%) so Excel's `0.0%` format renders them correctly.
 */
export const CFO_DRIVER_DEFS: CfoDriverDef[] = [
  {
    key: 'sales_growth_pct',
    label: 'Sales growth (annual)',
    unit: 'percent',
    namedRange: 'Sales_Growth',
    min: -0.5,
    max: 2.0,
    fallback: 0.1,
    description: 'Annualised revenue growth applied across the forecast horizon.',
    cashFavorable: 'up',
  },
  {
    key: 'gross_margin_pct',
    label: 'Gross margin',
    unit: 'percent',
    namedRange: 'Gross_Margin',
    min: 0,
    max: 1,
    fallback: 0.6,
    description: 'Gross profit as a percentage of revenue (COGS = revenue × (1 − margin)).',
    cashFavorable: 'up',
  },
  {
    key: 'dso_days',
    label: 'DSO (days sales outstanding)',
    unit: 'days',
    namedRange: 'DSO_Days',
    min: 0,
    max: 365,
    fallback: 45,
    description: 'Average days to collect receivables. AR = revenue × DSO / days-in-period.',
    cashFavorable: 'down',
  },
  {
    key: 'dpo_days',
    label: 'DPO (days payable outstanding)',
    unit: 'days',
    namedRange: 'DPO_Days',
    min: 0,
    max: 365,
    fallback: 30,
    description: 'Average days to pay suppliers. AP = COGS × DPO / days-in-period.',
    cashFavorable: 'up',
  },
  {
    key: 'opex_pct',
    label: 'Operating expense (% of revenue)',
    unit: 'percent',
    namedRange: 'Opex_Pct',
    min: 0,
    max: 2,
    fallback: 0.35,
    description: 'Operating expenses (ex-COGS) as a percentage of revenue.',
    cashFavorable: 'down',
  },
  {
    key: 'seasonality_strength',
    label: 'Seasonality strength',
    unit: 'ratio',
    namedRange: 'Seasonality',
    min: 0,
    max: 1,
    fallback: 0.2,
    description: '0 = flat monthly revenue; 1 = full seasonal swing around the trend.',
    cashFavorable: 'neutral',
  },
]

export type CfoDriverData = CfoDriverDef & {
  low: number
  expected: number
  high: number
  baseline: number
  note?: string
  /** Provenance: the company's trailing-12-mo actual in the driver's native units. */
  historicalActual?: number | null
  /** Human source label, e.g. "Your QB history" / "FuseGrid default". */
  sourceLabel?: string | null
  /** One-line grounding note, e.g. "DSO 47 days — derived from 12-mo QB AR ÷ revenue". */
  provenanceNote?: string | null
  /** True when grounded on < 6 closed months of QuickBooks history. */
  sparseHistory?: boolean
}

/**
 * Seed values (in expected case, at the forecast anchor) used to anchor the
 * driver model to the ML output. Extracted by the orchestrator where the ML
 * data lives, so the composer stays pure.
 */
export type CfoDriverModelSeed = {
  /** Expected revenue of the first forecast month. */
  baseMonthlyRevenue: number
  /** Opening cash balance at the forecast anchor. */
  openingCash: number
  /** Number of forecast months to project in the driver model. */
  horizonMonths: number
}

/**
 * Fixed 12-month seasonal index (mean 0) modulated by the Seasonality driver.
 * Kept as literals so the Excel formulas remain transparent and auditable.
 * Pattern: mild summer dip / Q4 lift, typical of SMB seasonality.
 */
export const CFO_SEASONAL_INDEX: number[] = [
  -0.04, -0.02, 0.01, 0.03, 0.05, 0.02,
  -0.03, -0.05, 0.0, 0.04, 0.08, 0.04,
]

export function seasonalIndexForMonthOffset(monthOffset: number): number {
  return CFO_SEASONAL_INDEX[((monthOffset % 12) + 12) % 12]
}

/** Clamp a driver value to its declared bounds. */
export function clampDriver(def: CfoDriverDef, value: number): number {
  if (!Number.isFinite(value)) return def.fallback
  return Math.min(def.max, Math.max(def.min, value))
}
